import React, { useEffect, useState, Component } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, MapPin, Bed, Bath, Square, CheckCircle, Circle,
  ExternalLink, Calendar, Building2, AlertTriangle, Brain,
  ThumbsUp, ThumbsDown, Lightbulb, Play, Droplets, Zap, Pencil, Check, X,
  Flame, TrendingUp, WrenchIcon, PoundSterling, Copy
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import ScoreGauge from '../components/ui/ScoreGauge';
import YieldMeter from '../components/ui/YieldMeter';
import PriceBandBadge from '../components/ui/PriceBandBadge';
import AreaBadge from '../components/ui/AreaBadge';
import SalesHistoryChart from '../components/charts/SalesHistoryChart';
import PriceComparisonChart from '../components/charts/PriceComparisonChart';
import ComparableSalesTable from '../components/charts/ComparableSalesTable';
import PriceHistogramChart from '../components/charts/PriceHistogramChart';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ImageGallery from '../components/ui/ImageGallery';
import ChartDataCallout, { ChartDataCalloutList } from '../components/ui/ChartDataCallout';
import { formatCurrency, formatYield, propertyTypeIcon } from '../utils/formatters';
import { pickPrimaryImage } from '../utils/propertyImages';
import { formatPriceReduction, hasPriceReduction } from '../utils/listingPrice';
import PropertyProfileCard from '../components/profile/PropertyProfileCard';
import { computeWeightedScore } from '../components/filters/ScoringSliders';
import useScoringWeights from '../hooks/useScoringWeights';
import { useAuth } from '../contexts/AuthContext';
import {
  hasBasicAccess,
  premiumFeatureHint,
  premiumUpgradeHref,
  premiumUpgradeLabel,
} from '../utils/access';
import { authFetch } from '../services/api';
import AiTopUpModal from '../components/ai/AiTopUpModal';

class ChartErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="h-48 flex items-center justify-center text-slate-600 text-sm">
          Chart unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

const VERDICT_STYLE = {
  STRONG_BUY: { bg: 'bg-emerald-500/15 border-emerald-500/30', text: 'text-emerald-300', label: 'Strong Buy' },
  BUY:        { bg: 'bg-teal-500/15 border-teal-500/30',       text: 'text-teal-300',    label: 'Buy'        },
  HOLD:       { bg: 'bg-amber-500/15 border-amber-500/30',     text: 'text-amber-300',   label: 'Hold'       },
  AVOID:      { bg: 'bg-red-500/15 border-red-500/30',         text: 'text-red-300',     label: 'Avoid'      },
};

const FLOOD_STYLE = {
  low:       'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  medium:    'text-amber-400 bg-amber-500/10 border-amber-500/30',
  high:      'text-orange-400 bg-orange-500/10 border-orange-500/30',
  'very high': 'text-red-400 bg-red-500/10 border-red-500/30',
};

function epcBandColour(band) {
  if (!band) return 'text-slate-500';
  const b = band.toUpperCase();
  if ('ABC'.includes(b)) return 'text-emerald-400';
  if ('DE'.includes(b)) return 'text-amber-400';
  return 'text-red-400'; // F, G
}

function epcEfficiencyColour(rating) {
  if (!rating) return 'text-slate-500';
  const r = rating.toLowerCase();
  if (r === 'very good' || r === 'good') return 'text-emerald-400';
  if (r === 'average') return 'text-amber-400';
  return 'text-red-400'; // poor, very poor
}

function epcAgeColour(ageBand) {
  if (!ageBand) return 'text-slate-500';
  const match = ageBand.match(/(\d{4})/);
  if (!match) return 'text-slate-500';
  const year = parseInt(match[1]);
  if (year >= 1990) return 'text-emerald-400';
  if (year >= 1950) return 'text-amber-400';
  return 'text-red-400'; // pre-1950
}

function PropertyDataPanel({ propertyId, score: initialScore, locked, lockedHint, upgradeHref, upgradeLabel }) {
  const [score, setScore] = useState(initialScore);
  const [loading, setLoading] = useState(false);

  const enrich = async () => {
    if (locked) return;
    setLoading(true);
    try {
      const r = await fetch(`/api/scoring/enrich/${propertyId}`, { method: 'POST' });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Enrichment failed'); }
      const data = await r.json();
      setScore(s => ({ ...s, ...data }));
      toast.success('PropertyData enrichment complete');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const enriched = score?.pd_enriched_at;
  const floodRisk = score?.pd_flood_risk?.toLowerCase();
  const floodStyle = FLOOD_STYLE[floodRisk] || 'text-slate-400 bg-slate-700/30 border-slate-600/30';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.12 }}
      className={clsx(
        'bg-slate-900 border border-slate-800 rounded-2xl p-6',
        locked && 'opacity-50',
      )}
    >
      <div className={clsx('flex items-center justify-between mb-4', locked && 'pointer-events-none select-none')}>
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-blue-400" />
          <h2 className="text-white font-semibold">PropertyData Enrichment</h2>
          {enriched && (
            <span className="text-xs text-slate-500">
              Updated {new Date(enriched).toLocaleDateString('en-GB')}
            </span>
          )}
        </div>
        <button
          onClick={enrich}
          disabled={loading || locked}
          title={locked ? lockedHint : undefined}
          className="flex items-center gap-1.5 text-xs bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <span className="animate-spin">⟳</span> : <Zap size={11} />}
          {enriched ? 'Re-enrich' : 'Enrich (3 credits)'}
        </button>
      </div>

      {locked && lockedHint && (
        <div className="pointer-events-auto text-center border-t border-slate-800 pt-4 mt-2">
          <p className="pointer-events-none text-slate-600 text-sm py-2">
            Live AVM, rental estimate, and flood risk from PropertyData.co.uk.
          </p>
          <p className="text-xs text-slate-500 mb-2">{lockedHint}</p>
          <Link to={upgradeHref} className="text-sm text-emerald-400 hover:text-emerald-300 font-semibold">
            {upgradeLabel}
          </Link>
        </div>
      )}

      {!locked && !enriched && !loading && (
        <p className="text-slate-600 text-sm py-3 text-center">
          Click "Enrich" to fetch live AVM, rental estimate and flood risk from PropertyData.co.uk.
        </p>
      )}

      {!locked && loading && (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-3 justify-center">
          <span className="animate-spin text-blue-400">⟳</span> Fetching from PropertyData…
        </div>
      )}

      {!locked && enriched && !loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-800/50 rounded-xl p-3">
            <div className="text-slate-500 text-xs mb-1">PD Valuation</div>
            <div className="text-blue-300 font-bold text-sm">
              {score.pd_avm ? formatCurrency(score.pd_avm) : '—'}
            </div>
            {score.pd_avm_lower && score.pd_avm_upper && (
              <div className="text-slate-600 text-xs mt-0.5">
                {formatCurrency(score.pd_avm_lower)} – {formatCurrency(score.pd_avm_upper)}
              </div>
            )}
          </div>
          <div className="bg-slate-800/50 rounded-xl p-3">
            <div className="text-slate-500 text-xs mb-1">PD Rent / mo</div>
            <div className="text-blue-300 font-bold text-sm">
              {score.pd_rental_estimate ? formatCurrency(score.pd_rental_estimate) : '—'}
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-3">
            <div className="text-slate-500 text-xs mb-1">Flood Risk</div>
            {floodRisk ? (
              <span className={clsx('inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded border capitalize', floodStyle)}>
                <Droplets size={10} /> {floodRisk}
              </span>
            ) : <span className="text-slate-600 text-sm">—</span>}
          </div>
          <div className="bg-slate-800/50 rounded-xl p-3">
            <div className="text-slate-500 text-xs mb-1">Revised Score</div>
            <div className="text-white font-bold text-sm">
              {score.investment_score != null ? `${score.investment_score.toFixed(0)}/100` : '—'}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}


function AIInsightPanel({ propertyId, insight: initialInsight, locked, lockedHint, upgradeHref, upgradeLabel }) {
  const [insight, setInsight] = useState(initialInsight || null);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);

  useEffect(() => {
    if (locked) return;
    authFetch('/api/ai/usage')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setUsage(d))
      .catch(() => {});
  }, [locked, propertyId]);

  const runAnalysis = async () => {
    if (locked) return;
    setLoading(true);
    try {
      const r = await authFetch(`/api/ai/analyse/property/${propertyId}`, { method: 'POST' });
      const body = await r.json().catch(() => ({}));
      if (r.status === 402) {
        const detail = body.detail || body;
        setUsage(detail.usage || detail);
        setShowTopUp(true);
        toast.error('AI allowance used up — buy credits to continue');
        return;
      }
      if (!r.ok) {
        const msg = typeof body.detail === 'string' ? body.detail : (body.detail?.message || 'Analysis failed');
        throw new Error(msg);
      }
      setInsight({
        ...body,
        positives: JSON.stringify(body.positives || []),
        risks: JSON.stringify(body.risks || []),
        property_highlights: JSON.stringify(body.property_highlights || []),
      });
      if (body.usage) setUsage(body.usage);
      toast.success('AI analysis complete');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const positives   = insight?.positives          ? (() => { try { return JSON.parse(insight.positives);          } catch { return []; } })() : [];
  const risks       = insight?.risks              ? (() => { try { return JSON.parse(insight.risks);              } catch { return []; } })() : [];
  const highlights  = insight?.property_highlights ? (() => { try { return JSON.parse(insight.property_highlights); } catch { return []; } })() : [];
  const vs = insight?.verdict ? VERDICT_STYLE[insight.verdict] : null;
  const lowIncluded =
    usage
    && !usage.unmetered
    && usage.includedRemaining != null
    && usage.includedRemaining < 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className={clsx(
        'bg-slate-900 border border-slate-800 rounded-2xl p-6',
        locked && 'opacity-50',
      )}
    >
      <AiTopUpModal
        open={showTopUp}
        onClose={() => setShowTopUp(false)}
        usage={usage}
        onPurchased={() => setShowTopUp(false)}
      />
      <div className={clsx('flex items-center justify-between mb-4', locked && 'pointer-events-none select-none')}>
        <div className="flex items-center gap-2">
          <Brain size={16} className="text-violet-400" />
          <h2 className="text-white font-semibold">AI Investment Analysis</h2>
          {vs && (
            <span className={clsx('text-xs font-bold px-2 py-0.5 rounded-full border', vs.bg, vs.text)}>
              {vs.label}
            </span>
          )}
        </div>
        <button
          onClick={runAnalysis}
          disabled={loading || locked}
          title={locked ? lockedHint : undefined}
          className="flex items-center gap-1.5 text-xs bg-violet-600/20 hover:bg-violet-600/30 text-violet-300 border border-violet-500/30 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <span className="animate-spin">⟳</span> : <Play size={11} />}
          {insight ? 'Re-analyse' : 'Analyse with AI'}
        </button>
      </div>

      {!locked && usage && !usage.unmetered && (
        <p className={clsx('text-xs mb-3', lowIncluded ? 'text-amber-400' : 'text-slate-500')}>
          Included left: {usage.includedRemaining ?? 0}/{usage.includedMonthly ?? 0}
          {' · '}
          Credits: {usage.creditBalance ?? 0}
        </p>
      )}

      {locked && lockedHint && (
        <div className="pointer-events-auto text-center border-t border-slate-800 pt-4 mt-2">
          <p className="pointer-events-none text-slate-600 text-sm py-2">
            AI-powered investment verdict, risks, and location notes for this listing.
          </p>
          <p className="text-xs text-slate-500 mb-2">{lockedHint}</p>
          <Link to={upgradeHref} className="text-sm text-emerald-400 hover:text-emerald-300 font-semibold">
            {upgradeLabel}
          </Link>
        </div>
      )}

      {!locked && !insight && !loading && (
        <p className="text-slate-600 text-sm py-4 text-center">
          Click "Analyse with AI" to get an AI-powered investment verdict for this property.
        </p>
      )}

      {!locked && loading && (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-4 justify-center">
          <span className="animate-spin text-violet-400">⟳</span> Running AI analysis…
        </div>
      )}

      {!locked && insight && !loading && (
        <div className="space-y-4">
          {/* Confidence */}
          {insight.confidence != null && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              Confidence: <span className="text-slate-300">{Math.round(insight.confidence * 100)}%</span>
              <div className="flex-1 bg-slate-800 rounded-full h-1.5 max-w-[120px]">
                <div className={clsx('h-1.5 rounded-full', vs?.text?.replace('text-', 'bg-') || 'bg-slate-500')}
                  style={{ width: `${Math.round(insight.confidence * 100)}%` }} />
              </div>
            </div>
          )}

          {/* Summary */}
          {insight.summary && (
            <p className="text-slate-300 text-sm leading-relaxed">{insight.summary}</p>
          )}

          {/* Property highlights extracted from listing description */}
          {highlights.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {highlights.map((h, i) => (
                <span key={i} className="text-xs bg-slate-800 text-slate-300 border border-slate-700 rounded-lg px-2.5 py-1">
                  {h}
                </span>
              ))}
            </div>
          )}

          {/* Location notes */}
          {insight.location_notes && (
            <div className="bg-slate-800/50 rounded-xl p-3">
              <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1.5 font-medium uppercase tracking-wider">
                <Lightbulb size={11} /> Location Notes
              </div>
              <p className="text-slate-400 text-xs leading-relaxed">{insight.location_notes}</p>
            </div>
          )}

          {/* Positives & Risks */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {positives.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs text-emerald-400 mb-1.5 font-medium">
                  <ThumbsUp size={11} /> Positives
                </div>
                <ul className="space-y-1">
                  {positives.map((p, i) => (
                    <li key={i} className="text-xs text-slate-400 flex gap-1.5">
                      <span className="text-emerald-600 mt-0.5 shrink-0">✓</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {risks.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs text-red-400 mb-1.5 font-medium">
                  <ThumbsDown size={11} /> Risks
                </div>
                <ul className="space-y-1">
                  {risks.map((r, i) => (
                    <li key={i} className="text-xs text-slate-400 flex gap-1.5">
                      <span className="text-red-600 mt-0.5 shrink-0">✗</span> {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Footer */}
          {insight.generated_at && (
            <p className="text-slate-700 text-xs border-t border-slate-800 pt-2">
              Analysed {new Date(insight.generated_at).toLocaleString('en-GB')}
              {insight.tokens_used && ` · ${insight.tokens_used.toLocaleString()} tokens`}
            </p>
          )}
        </div>
      )}
    </motion.div>
  );
}

const EPC_RATING_COLORS = {
  A: 'bg-emerald-600 text-white',
  B: 'bg-emerald-500 text-white',
  C: 'bg-green-500 text-white',
  D: 'bg-yellow-400 text-slate-900',
  E: 'bg-amber-500 text-white',
  F: 'bg-orange-600 text-white',
  G: 'bg-red-700 text-white',
};

function EPCRatingBadge({ rating, label }) {
  const cls = EPC_RATING_COLORS[rating?.toUpperCase()] || 'bg-slate-700 text-slate-400';
  return (
    <div className="text-center">
      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg text-lg font-extrabold ${cls}`}>
        {rating || '?'}
      </div>
      {label && <div className="text-slate-500 text-xs mt-1">{label}</div>}
    </div>
  );
}

function EPCPanel({ property }) {
  const {
    epc_energy_rating: current,
    epc_potential_rating: potential,
    epc_floor_area_sqm,
    epc_inspection_date,
    epc_compliance_cost_low: costLow,
    epc_compliance_cost_high: costHigh,
  } = property;

  if (!current && !epc_floor_area_sqm) return null;

  const nonCompliant = current && ['F', 'G'].includes(current.toUpperCase());
  const hasCost = costLow != null || costHigh != null;

  const fmt = v => v != null ? `£${v.toLocaleString('en-GB')}` : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.11 }}
      className={`border rounded-2xl p-6 ${nonCompliant
        ? 'bg-red-950/30 border-red-800/40'
        : 'bg-slate-900 border-slate-800'}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <Flame size={16} className={nonCompliant ? 'text-red-400' : 'text-amber-400'} />
        <h2 className="text-white font-semibold">Energy Performance Certificate</h2>
        {epc_inspection_date && (
          <span className="text-xs text-slate-500">
            Inspected {new Date(epc_inspection_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
          </span>
        )}
        {nonCompliant && (
          <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border bg-red-500/15 border-red-500/40 text-red-300">
            <AlertTriangle size={11} /> Non-compliant for rental
          </span>
        )}
      </div>

      <div className="flex items-center gap-6 mb-5">
        <EPCRatingBadge rating={current} label="Current" />
        {potential && potential !== current && (
          <>
            <div className="flex flex-col items-center gap-1">
              <TrendingUp size={16} className="text-slate-500" />
              <span className="text-slate-600 text-xs">potential</span>
            </div>
            <EPCRatingBadge rating={potential} label="Potential" />
          </>
        )}
        {epc_floor_area_sqm && (
          <div className="ml-4 text-center">
            <div className="text-white font-bold text-lg">{Math.round(epc_floor_area_sqm)} m²</div>
            <div className="text-slate-500 text-xs">Floor Area</div>
          </div>
        )}
      </div>

      {nonCompliant && (
        <div className="rounded-xl border border-red-800/40 bg-red-950/40 p-4 space-y-2">
          <div className="flex items-center gap-2 text-red-300 text-sm font-medium">
            <WrenchIcon size={14} /> EPC Compliance Work Required
          </div>
          <p className="text-slate-400 text-xs leading-relaxed">
            Properties rated {current} cannot be legally rented under the Minimum Energy Efficiency
            Standards (MEES). Improvement work must be completed before letting.
          </p>
          {hasCost && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div className="bg-slate-900/60 rounded-lg p-3">
                <div className="text-slate-500 text-xs mb-0.5">Est. Cost (Low)</div>
                <div className="text-amber-400 font-bold">{fmt(costLow) ?? '—'}</div>
              </div>
              <div className="bg-slate-900/60 rounded-lg p-3">
                <div className="text-slate-500 text-xs mb-0.5">Est. Cost (High)</div>
                <div className="text-red-400 font-bold">{fmt(costHigh) ?? '—'}</div>
              </div>
            </div>
          )}
          {!hasCost && (
            <p className="text-slate-600 text-xs">
              Import EPC recommendations data to see estimated improvement costs.
            </p>
          )}
        </div>
      )}

      {!nonCompliant && current && (
        <p className="text-slate-500 text-xs">
          Rating {current} — compliant for rental (minimum E required).
          {potential && potential !== current && ` Could reach ${potential} with improvements.`}
        </p>
      )}
    </motion.div>
  );
}

const WHARF_URL = 'https://propertyfundingplatform.com/WharfFinancial#!/allloans';

function FundingQuoteButton({ property, score }) {
  const [open, setOpen] = useState(false);

  const isBmv = score?.price_band === 'brilliant' || score?.price_band === 'good';
  const isHmo = property.property_type?.toLowerCase().includes('hmo') ||
    (score?.hmo_opportunity_score > 5);
  const purchaseCosts = property.asking_price ? Math.round(property.asking_price * 0.05) : null;

  const rows = [
    { label: 'Loan type',       value: 'Bridging → PURCHASE/RE-FI' },
    { label: 'Main residence',  value: 'No' },
    { label: 'Postcode',        value: property.postcode || '—' },
    { label: 'Plot type',       value: 'Existing Build' },
    { label: 'Current use',     value: isHmo ? 'Licenced HMO' : 'Pure Residential' },
    { label: 'BMV purchase',    value: isBmv ? 'Yes' : 'No' },
    { label: 'Purchase price',  value: property.asking_price ? `£${property.asking_price.toLocaleString('en-GB')}` : '—' },
    { label: 'Purchase costs',  value: purchaseCosts ? `£${purchaseCosts.toLocaleString('en-GB')} (5%)` : '—' },
    { label: 'Loan term',       value: '12 months' },
    { label: 'Max loan',        value: 'Yes' },
  ];

  const copyAll = () => {
    const text = rows.map(r => `${r.label}: ${r.value}`).join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/30 rounded-lg px-3 py-1.5 transition-colors"
      >
        <PoundSterling size={11} /> Get Funding Quote
      </button>

      {open && (
        <>
          {/* backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-50 w-72 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-white text-sm font-semibold">Wharf Financial — enter these values</span>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X size={14} /></button>
            </div>

            <div className="space-y-1.5 mb-3">
              {rows.map(({ label, value }) => (
                <div key={label} className="flex justify-between text-xs">
                  <span className="text-slate-500">{label}</span>
                  <span className="text-slate-200 font-medium text-right max-w-[150px]">{value}</span>
                </div>
              ))}
            </div>

            <div className="flex gap-2 border-t border-slate-800 pt-3">
              <button
                onClick={copyAll}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white border border-slate-700 rounded-lg px-2.5 py-1.5 transition-colors"
              >
                <Copy size={10} /> Copy
              </button>
              <a
                href={WHARF_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex-1 flex items-center justify-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg px-3 py-1.5 transition-colors font-medium"
              >
                <ExternalLink size={10} /> Open Wharf Financial
              </a>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const UK_POSTCODE = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s*\d[A-Z]{2}$/i;

function PostcodeEditor({ propertyId, currentPostcode, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const open = () => { setValue(currentPostcode || ''); setEditing(true); };
  const cancel = () => setEditing(false);

  const save = async () => {
    const pc = value.trim().toUpperCase();
    if (!UK_POSTCODE.test(pc)) {
      toast.error('Enter a valid UK postcode (e.g. NG1 5AB)');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`/api/properties/${propertyId}/postcode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode: pc }),
      });
      if (!r.ok) { const e = await r.json(); throw new Error(e.detail || 'Failed'); }
      toast.success('Postcode saved — property re-scored');
      setEditing(false);
      onSaved(pc);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <input
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
          placeholder="e.g. NG1 5AB"
          maxLength={8}
          className="bg-slate-800 border border-slate-600 focus:border-emerald-500 text-white text-sm rounded-lg px-2 py-0.5 w-28 outline-none"
        />
        <button onClick={save} disabled={saving} className="text-emerald-400 hover:text-emerald-300 disabled:opacity-50">
          {saving ? <span className="animate-spin inline-block">⟳</span> : <Check size={14} />}
        </button>
        <button onClick={cancel} className="text-slate-500 hover:text-slate-300"><X size={14} /></button>
      </span>
    );
  }

  if (currentPostcode) {
    return (
      <span className="inline-flex items-center gap-1 group">
        <span>{currentPostcode}</span>
        <button onClick={open} className="opacity-0 group-hover:opacity-100 text-slate-600 hover:text-slate-400 transition-opacity">
          <Pencil size={11} />
        </button>
      </span>
    );
  }

  return (
    <button
      onClick={open}
      className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg px-2 py-0.5 text-xs transition-colors"
    >
      <MapPin size={11} /> Add postcode
    </button>
  );
}


export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { weights, hasWeights } = useScoringWeights();
  const canAccessPremium = hasBasicAccess(user);
  const premiumLocked = !canAccessPremium;
  const premiumHint = premiumFeatureHint(user);
  const premiumHref = premiumUpgradeHref(user);
  const premiumLabel = premiumUpgradeLabel(user);
  const [property, setProperty] = useState(null);
  const [areaData, setAreaData] = useState(null);
  const [areaStats, setAreaStats] = useState(null);
  const [nationalData, setNationalData] = useState(null);
  const [comparables, setComparables] = useState(null);
  const [priceDistribution, setPriceDistribution] = useState(null);
  const [avm, setAvm] = useState(null);
  const [avmLoading, setAvmLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);

  const saveDetails = async (price, current_usage) => {
    setSavingDetails(true);
    try {
      const token = localStorage.getItem('assetlens_token');
      const r = await fetch(`/api/properties/${id}/details`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ price: price === '' ? null : price, current_usage: current_usage || '' }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
        return;
      }
      const updated = await r.json();
      setProperty(prev => ({
        ...prev,
        asking_price: updated.asking_price,
        current_usage: updated.current_usage,
        // If a guide_price was returned and there's a latest auction, mirror it onto the auctions array
        auctions: updated.guide_price != null && prev.auctions?.length
          ? [{ ...prev.auctions[prev.auctions.length - 1], guide_price: updated.guide_price }, ...prev.auctions.slice(0, -1)]
          : prev.auctions,
      }));
      setEditingDetails(false);
      toast.success('Saved');
    } catch (e) {
      toast.error(e.message || 'Network error');
    } finally {
      setSavingDetails(false);
    }
  };

  const loadAreaData = (pc, pt, gp) => {
    const enc = encodeURIComponent(pc);
    // guide_price is Optional[int] server-side — sending an empty string fails
    // Pydantic coercion with a 422. Only attach the param when we actually
    // have a numeric value.
    const distUrl = gp
      ? `/api/areas/${enc}/price-distribution?guide_price=${encodeURIComponent(gp)}`
      : `/api/areas/${enc}/price-distribution`;
    Promise.allSettled([
      fetch(`/api/areas/${enc}/trends`).then(r => r.ok ? r.json() : null),
      fetch(`/api/areas/${enc}/stats`).then(r => r.ok ? r.json() : null),
      fetch(`/api/areas/national/trends`).then(r => r.ok ? r.json() : null),
      fetch(`/api/areas/${enc}/comparables?property_type=${encodeURIComponent(pt)}`).then(r => r.ok ? r.json() : null),
      fetch(distUrl).then(r => r.ok ? r.json() : null),
    ]).then(([trends, stats, national, comps, dist]) => {
      if (trends.value) setAreaData(trends.value);
      if (stats.value) setAreaStats(stats.value);
      if (national.value) setNationalData(national.value);
      if (comps.value) setComparables(comps.value);
      if (dist.value) setPriceDistribution(dist.value);
    });
  };

  useEffect(() => {
    authFetch(`/api/properties/${id}`)
      .then((r) => {
        if (!r.ok) {
          throw new Error(r.status === 404 ? 'Property not found' : `Failed to load property (${r.status})`);
        }
        return r.json();
      })
      .then((prop) => {
        setProperty(prop);
        if (prop.postcode) {
          loadAreaData(prop.postcode, prop.property_type || '', prop.asking_price || '');
        }
      })
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  // Check whether this caller is allowed to edit price + usage. Quietly
  // returns false for unauthenticated users — the edit UI just stays hidden.
  useEffect(() => {
    const token = localStorage.getItem('assetlens_token');
    if (!token) return;
    fetch(`/api/properties/${id}/can-edit`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCanEdit(!!d.can_edit); })
      .catch(() => {});
  }, [id]);

  // Comparables-based AVM — matches on postcode sector + type + beds + sqm.
  useEffect(() => {
    if (!id || !canAccessPremium) {
      setAvm(null);
      setAvmLoading(false);
      return;
    }
    setAvmLoading(true);
    authFetch(`/api/properties/${id}/comparables`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setAvm(data); })
      .catch(() => {})
      .finally(() => setAvmLoading(false));
  }, [id, canAccessPremium]);

  const toggleReview = async () => {
    setReviewLoading(true);
    try {
      const r = await authFetch(`/api/properties/${id}/review`, { method: 'POST' });
      const data = await r.json();
      setProperty(p => ({ ...p, is_reviewed: data.is_reviewed, reviewed_at: data.reviewed_at }));
      toast.success(data.message);
    } catch (e) {
      toast.error('Failed to update review status');
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-96">
      <LoadingSpinner size={40} className="text-emerald-500" />
    </div>
  );

  if (!property) return (
    <div className="p-8 text-center">
      <AlertTriangle size={40} className="text-amber-400 mx-auto mb-3" />
      <p className="text-slate-400">Property not found</p>
    </div>
  );

  const score = property.score;
  // Personalised "Your Score" — match the value shown on the list view so
  // there's no apparent discrepancy when a user clicks through. If the user
  // has saved scoring preferences (or anonymous browser-stored ones) we
  // surface their personal score; otherwise we fall back to the raw global
  // investment score.
  const personalScore = hasWeights && weights
    ? computeWeightedScore({ ...property, score }, weights)
    : null;
  const showPersonal  = personalScore != null && Number.isFinite(personalScore);

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-10">
      <ImageGallery
        imageUrl={pickPrimaryImage(property)}
        imageUrls={property.image_urls}
        alt={property.address}
      />

      <div className="px-6 space-y-6">
      {/* Property description */}
      {property.description && (
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
          <h3 className="text-sm font-semibold text-slate-300 mb-2">Description</h3>
          <p className="text-sm text-slate-400 leading-relaxed whitespace-pre-line">
            {property.description}
          </p>
        </div>
      )}

      {/* Back + header */}
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
        <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="mt-1 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors shrink-0"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              <span className="text-2xl">{propertyTypeIcon(property.property_type)}</span>
              {score && <PriceBandBadge band={score.price_band} />}
              <AreaBadge property={property} size="md" />
              {property.is_reviewed && (
                <span className="inline-flex items-center gap-1.5 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full">
                  <CheckCircle size={13} /> Reviewed
                </span>
              )}
            </div>
            <h1 className="text-xl font-bold text-white leading-snug break-words">{property.address}</h1>
            <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 mt-1 text-slate-400 text-sm">
              <MapPin size={14} className={property.postcode ? '' : 'text-amber-400'} />
              <PostcodeEditor
                propertyId={id}
                currentPostcode={property.postcode}
                onSaved={pc => {
                  setProperty(p => ({ ...p, postcode: pc }));
                  loadAreaData(pc, property.property_type || '', property.asking_price || '');
                }}
              />
              {property.town && <><span>·</span><span>{property.town}</span></>}
              {property.county && <><span>·</span><span>{property.county}</span></>}
            </div>
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:shrink-0">
          <FundingQuoteButton property={property} score={score} />
          <button
            onClick={toggleReview}
            disabled={reviewLoading}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
              property.is_reviewed
                ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20'
                : 'bg-slate-800 border border-slate-700 text-slate-300 hover:text-white hover:bg-slate-700'
            }`}
          >
            {reviewLoading ? <LoadingSpinner size={14} /> : property.is_reviewed ? <CheckCircle size={14} /> : <Circle size={14} />}
            {property.is_reviewed ? 'Reviewed' : 'Mark Reviewed'}
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: score meters */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-6"
        >
          <h2 className="text-white font-semibold mb-5">Investment Analysis</h2>

          {!score ? (
            <div className="text-center py-8 px-4 border border-dashed border-slate-700 rounded-xl">
              <p className="text-slate-300 text-sm mb-2">Scores, AI insights, and charts are included with Basic or Pro.</p>
              <p className="text-slate-500 text-xs mb-4">Start a 7-day free trial — card required, no charge until trial ends.</p>
              <Link to="/choose-plan" className="inline-block bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-5 py-2.5 rounded-xl">
                View plans
              </Link>
            </div>
          ) : (
          <>
          <div className="flex justify-around mb-6">
            <ScoreGauge
              score={showPersonal ? personalScore : score?.investment_score}
              size={120}
              label={showPersonal ? 'Your Score' : 'Overall Score'}
            />
            <YieldMeter yieldPct={score?.gross_yield_pct} size={100} label="Gross Yield" />
          </div>

          {showPersonal && score?.investment_score != null && (
            <p className="text-[10px] text-slate-500 -mt-2 mb-4 text-center">
              Personalised by your scoring sliders. Standard score: {score.investment_score.toFixed(0)}/100.
            </p>
          )}

          {/* Score breakdown */}
          {score && (
            <div className="space-y-3 border-t border-slate-800 pt-4">
              {[
                { label: 'Price Score', value: score.price_score, max: 40, color: '#10b981' },
                { label: 'Yield Score', value: score.yield_score, max: 30, color: '#22c55e' },
                { label: 'Area Trend', value: score.area_trend_score, max: 20, color: '#f59e0b' },
                { label: 'HMO Score', value: score.hmo_opportunity_score, max: 10, color: '#a78bfa' },
              ].map(({ label, value, max, color }) => (
                <div key={label}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-slate-400">{label}</span>
                    <span className="font-medium" style={{ color }}>{value?.toFixed(0) ?? '—'}/{max}</span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-1.5">
                    <div
                      className="h-1.5 rounded-full transition-all duration-700"
                      style={{ width: `${value != null ? (value / max) * 100 : 0}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
          </>
          )}
        </motion.div>

        {/* Center: price comparison + details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5"
        >
          <h2 className="text-white font-semibold">Price Analysis</h2>

          <EditableDetails
            property={property}
            canEdit={canEdit}
            editing={editingDetails}
            saving={savingDetails}
            onEdit={() => setEditingDetails(true)}
            onCancel={() => setEditingDetails(false)}
            onSave={saveDetails}
          />

          {/* Price comparison chart */}
          <PriceComparisonChart
            askingPrice={property.asking_price}
            estimatedValue={score?.estimated_value}
          />

          {/* Key figures */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-800/50 rounded-xl p-3 col-span-2 sm:col-span-1">
              <div className="text-slate-500 text-xs mb-1">Asking Price</div>
              <div className="text-amber-400 font-bold">{formatCurrency(property.asking_price)}</div>
              {hasPriceReduction(property) && (
                <p className="text-emerald-400 text-xs mt-1.5 leading-snug">
                  {formatPriceReduction(property)}
                </p>
              )}
              {property.sources?.[0]?.source_url && (
                <a
                  href={property.sources[0].source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-emerald-400 mt-2"
                >
                  View live listing <ExternalLink size={11} />
                </a>
              )}
            </div>
            <div className="bg-slate-800/50 rounded-xl p-3 col-span-2 sm:col-span-1">
              <div className="text-slate-500 text-xs mb-1">Est. Market Value</div>
              <div className="text-emerald-400 font-bold">{score?.estimated_value ? formatCurrency(score.estimated_value) : '—'}</div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-3">
              <div className="text-slate-500 text-xs mb-1">Price Deviation</div>
              <div className={`font-bold ${score?.price_deviation_pct < 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {score?.price_deviation_pct != null
                  ? `${(score.price_deviation_pct * 100).toFixed(1)}%`
                  : '—'}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-3">
              <div className="text-slate-500 text-xs mb-1">Est. Monthly Rent</div>
              <div className="text-blue-400 font-bold">{formatCurrency(score?.estimated_monthly_rent)}</div>
            </div>
          </div>

          {score?.estimated_value && (
            <ChartDataCallout tone="emerald" title="Est. Market Value vs area averages" className="mt-3">
              This figure is our <strong className="text-slate-200">model estimate for this property</strong>.
              The area trend boxes on the right are district-wide averages — a £800k detached can sit
              far above a £470k district mean without being &quot;wrong&quot;.
            </ChartDataCallout>
          )}

          {/* Property specs */}
          <div className="border-t border-slate-800 pt-4 grid grid-cols-3 gap-3 text-center">
            <div>
              <Bed size={18} className="text-slate-400 mx-auto mb-1" />
              <div className="text-white font-semibold">{property.bedrooms ?? '—'}</div>
              <div className="text-slate-500 text-xs">Bedrooms</div>
            </div>
            <div>
              <Bath size={18} className="text-slate-400 mx-auto mb-1" />
              <div className="text-white font-semibold">{property.bathrooms ?? '—'}</div>
              <div className="text-slate-500 text-xs">Bathrooms</div>
            </div>
            <div>
              <Square size={18} className="text-slate-400 mx-auto mb-1" />
              <div className="text-white font-semibold">{property.floor_area_sqm ? `${property.floor_area_sqm}m²` : '—'}</div>
              <div className="text-slate-500 text-xs">Floor Area</div>
            </div>
          </div>
        </motion.div>

        {/* Right: sources & area stats */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5"
        >
          <h2 className="text-white font-semibold">Area Market Trends</h2>
          <ChartDataCallout tone="info" title="District averages — not this home's value" className="mt-3">
            Figures below are the <strong className="text-slate-200">average of all residential sales</strong> in
            this postcode district (Land Registry). They show how the area has moved over time — not what
            this specific property is worth. Use Indicative Value and comparable sales for this home.
          </ChartDataCallout>
          {(areaStats || score?.area_avg_price) ? (
            <div className="space-y-3">
              <div className="text-slate-500 text-xs font-medium uppercase tracking-wider">
                {areaData?.district || property.postcode?.split(' ')[0]} · district-wide sales
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Avg area sale (1yr)',  value: areaStats?.avg_price_1yr  || score?.area_avg_price, fmt: true },
                  { label: 'Avg area sale (5yr)',  value: areaStats?.avg_price_5yr,  fmt: true },
                  { label: '5yr Growth', value: areaStats?.growth_pct_5yr  || score?.area_growth_10yr_pct,
                    render: v => <span className={v >= 0 ? 'text-emerald-400' : 'text-red-400'}>{v >= 0 ? '+' : ''}{(v * 100).toFixed(1)}%</span> },
                  { label: '10yr Growth',value: areaStats?.growth_pct_10yr,
                    render: v => <span className={v >= 0 ? 'text-emerald-400' : 'text-red-400'}>{v >= 0 ? '+' : ''}{(v * 100).toFixed(1)}%</span> },
                  { label: 'Sales (1yr)',value: areaStats?.transaction_count_1yr,
                    render: v => <span className="text-slate-200">{v?.toLocaleString()}</span> },
                  { label: 'Sales (10yr)',value: areaStats?.transaction_count_10yr,
                    render: v => <span className="text-slate-200">{v?.toLocaleString()}</span> },
                ].map(({ label, value, fmt, render }) => value != null && (
                  <div key={label} className="bg-slate-800/50 rounded-xl p-2.5">
                    <div className="text-slate-500 text-[10px] mb-0.5">{label}</div>
                    <div className="font-bold text-sm">
                      {render ? render(value) : fmt ? formatCurrency(value, true) : value}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : property.postcode ? (
            <p className="text-slate-600 text-sm">Loading area data…</p>
          ) : (
            <p className="text-slate-600 text-sm">Add a postcode to see area statistics.</p>
          )}

          {/* Sources */}
          {property.sources?.length > 0 && (
            <div>
              <div className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-2">Listing Sources</div>
              <div className="space-y-2">
                {property.sources.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300 capitalize">{s.source_name}</span>
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${s.is_active ? 'bg-emerald-400' : 'bg-slate-600'}`} />
                      {s.source_url && (
                        <a href={s.source_url} target="_blank" rel="noopener noreferrer"
                          className="text-slate-500 hover:text-slate-300">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Last seen */}
          <div className="border-t border-slate-800 pt-4 text-xs text-slate-500 flex items-center gap-1.5">
            <Calendar size={12} />
            Found: {property.date_found ? new Date(property.date_found).toLocaleDateString('en-GB') : '—'}
          </div>
        </motion.div>
      </div>

      {/* Property Attribute Profile */}
      <PropertyProfileCard propertyId={id} />

      {/* EPC Panel */}
      <EPCPanel property={property} />

      {/* Neighbourhood Panel */}
      {property.neighbourhood_enriched_at && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4"
        >
          <h2 className="text-white font-semibold flex items-center gap-2">
            <MapPin size={18} className="text-blue-400" /> Neighbourhood
          </h2>

          {/* Quick stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {property.broadband_gigabit_pct != null && (
              <div className="bg-slate-800/60 rounded-lg p-3">
                <div className="text-xs text-slate-500">Gigabit Broadband</div>
                <div className={`text-lg font-bold ${property.broadband_gigabit_pct >= 80 ? 'text-emerald-400' : property.broadband_gigabit_pct >= 30 ? 'text-amber-400' : 'text-red-400'}`}>
                  {property.broadband_gigabit_pct}%
                </div>
              </div>
            )}
            {property.crime_rate_band && (
              <div className="bg-slate-800/60 rounded-lg p-3">
                <div className="text-xs text-slate-500">Crime Rate</div>
                <div className="text-lg font-bold text-white">{property.crime_rate_band}</div>
                {property.crime_trend && <div className="text-xs text-slate-500">{property.crime_trend}</div>}
              </div>
            )}
            {property.imd_rank && (
              <div className="bg-slate-800/60 rounded-lg p-3">
                <div className="text-xs text-slate-500">Deprivation Rank</div>
                <div className="text-lg font-bold text-white">{property.imd_rank.toLocaleString()}</div>
                <div className="text-xs text-slate-500">of 32,844</div>
              </div>
            )}
            {property.nearest_station_name && (
              <div className="bg-slate-800/60 rounded-lg p-3">
                <div className="text-xs text-slate-500">Nearest Station</div>
                <div className="text-sm font-bold text-white">{property.nearest_station_name}</div>
                <div className="text-xs text-slate-500">{property.nearest_station_distance_mi} mi</div>
              </div>
            )}
          </div>

          {/* Schools */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {property.nearest_primary_name && (
              <div className="bg-slate-800/60 rounded-lg p-3">
                <div className="text-xs text-slate-500">Nearest Primary</div>
                <div className="text-sm font-medium text-white">{property.nearest_primary_name}</div>
                <div className="text-xs text-slate-500">{property.nearest_primary_distance_mi} mi</div>
              </div>
            )}
            {property.nearest_secondary_name && (
              <div className="bg-slate-800/60 rounded-lg p-3">
                <div className="text-xs text-slate-500">Nearest Secondary</div>
                <div className="text-sm font-medium text-white">{property.nearest_secondary_name}</div>
                <div className="text-xs text-slate-500">{property.nearest_secondary_distance_mi} mi</div>
              </div>
            )}
          </div>

          {/* Planning flags */}
          <div className="flex flex-wrap gap-2">
            {property.in_conservation_area && (
              <span className="text-xs px-2.5 py-1 bg-amber-500/15 text-amber-400 rounded-full border border-amber-500/30">Conservation Area</span>
            )}
            {property.in_flood_zone && (
              <span
                className="text-xs px-2.5 py-1 bg-red-500/15 text-red-400 rounded-full border border-red-500/30"
                title="Postcode point intersects an Environment Agency flood-risk polygon"
              >
                In Flood Zone {property.in_flood_zone}
              </span>
            )}
            {property.in_green_belt && (
              <span className="text-xs px-2.5 py-1 bg-green-500/15 text-green-400 rounded-full border border-green-500/30">Green Belt</span>
            )}
            {property.has_article4 && (
              <span className="text-xs px-2.5 py-1 bg-purple-500/15 text-purple-400 rounded-full border border-purple-500/30">Article 4 Direction</span>
            )}
            {property.is_listed_building && (() => {
              const dist = property.listed_building_distance_m;
              const grade = property.is_listed_building;
              const veryClose = dist == null || dist <= 30;
              const label = veryClose
                ? `Listed (Grade ${grade})`
                : `Near a Listed Grade ${grade}`;
              const distText = dist != null
                ? (veryClose ? `at or directly adjacent (${dist}m)` : `${dist}m away`)
                : 'in the immediate vicinity';
              const tooltip = (
                `A Grade ${grade} listed building is ${distText}. ` +
                `${veryClose ? 'Listed-building consent is required for almost any external or significant internal alteration. ' : ''}` +
                `Even when the property itself isn't listed, proximity to a listed building can affect planning permissions, ` +
                `conservation considerations, and what extensions or external changes are allowed.`
              );
              return (
                <span title={tooltip}
                  className="text-xs px-2.5 py-1 bg-blue-500/15 text-blue-400 rounded-full border border-blue-500/30 cursor-help">
                  {label}
                </span>
              );
            })()}
            {!property.in_conservation_area && !property.in_flood_zone && !property.in_green_belt && !property.has_article4 && !property.is_listed_building && (
              <span className="text-xs px-2.5 py-1 bg-emerald-500/15 text-emerald-400 rounded-full border border-emerald-500/30">No Planning Restrictions</span>
            )}
          </div>

          {/* Link to full report */}
          {property.postcode && (
            <a
              href={`/neighbourhood/${encodeURIComponent(property.postcode)}`}
              className="text-sm text-blue-400 hover:text-blue-300 transition"
            >
              View full neighbourhood report &rarr;
            </a>
          )}
        </motion.div>
      )}

      {/* PropertyData Enrichment Panel */}
      <PropertyDataPanel
        propertyId={id}
        score={property.score}
        locked={premiumLocked}
        lockedHint={premiumHint}
        upgradeHref={premiumHref}
        upgradeLabel={premiumLabel}
      />

      {/* AI Analysis Panel */}
      <AIInsightPanel
        propertyId={id}
        insight={property.ai_insight}
        locked={premiumLocked}
        lockedHint={premiumHint}
        upgradeHref={premiumHref}
        upgradeLabel={premiumLabel}
      />

      {/* 10-year history chart + volume + national comparison */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-slate-900 border border-slate-800 rounded-2xl p-6"
      >
        <div className="mb-4">
          <h2 className="text-white font-semibold">10-Year Price History</h2>
        </div>
        <ChartDataCallout tone="info" title="District trend vs national" className="mb-4">
          Tracks how <strong className="text-slate-200">average sold prices</strong> in this postcode district
          have moved over ten years, compared to England &amp; Wales. Grey bars show how many sales
          happened each year — market activity, not price.
        </ChartDataCallout>
        <ChartErrorBoundary>
          <SalesHistoryChart
            data={areaData?.sales_by_year}
            currentPrice={property.asking_price}
            nationalData={nationalData}
          />
        </ChartErrorBoundary>
        <p className="text-slate-600 text-xs mt-3">
          Source: Land Registry Price Paid Data © Crown copyright and database right 2026
        </p>
      </motion.div>

      {/* Comparables-based AVM — hero result, full width */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.22 }}
        className="bg-slate-900 border border-slate-800 rounded-2xl p-6"
      >
        <ComparablesAvm
          avm={avm}
          loading={avmLoading}
          guidePrice={property.asking_price}
          locked={premiumLocked}
          lockedHint={premiumHint}
          upgradeHref={premiumHref}
          upgradeLabel={premiumLabel}
          property={property}
        />
      </motion.div>

      {/* Price distribution + comparable sales — side by side on wide screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Price distribution histogram */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-6"
        >
          <h2 className="text-white font-semibold mb-3">District Price Distribution</h2>
          {priceDistribution?.percentile != null && (
            <p className="text-sm font-medium text-slate-300 mb-3">
              Asking price sits at the{' '}
              <span className={priceDistribution.percentile < 30 ? 'text-emerald-400' : priceDistribution.percentile > 70 ? 'text-amber-400' : 'text-slate-200'}>
                {priceDistribution.percentile}th percentile
              </span>
              {' '}of all district sales (last 3 years)
            </p>
          )}
          <ChartErrorBoundary>
            <PriceHistogramChart
              data={priceDistribution}
              guidePrice={property.asking_price}
              district={property.postcode?.split(' ')[0]}
            />
          </ChartErrorBoundary>
        </motion.div>

        {/* Comparable sales table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-900 border border-slate-800 rounded-2xl p-6"
        >
          <h2 className="text-white font-semibold mb-3">Comparable Sales</h2>
          <ChartDataCallout tone="info" title="Recent same-type sales" className="mb-4">
            Land Registry <strong className="text-slate-200">completed sales</strong> of
            {property.property_type ? ` ${property.property_type}` : ''} homes in{' '}
            <strong className="text-slate-200">{property.postcode?.split(' ')[0] || 'this district'}</strong> over
            the last 3 years. Compare sold prices to this listing&apos;s asking price — not to district averages.
          </ChartDataCallout>
          <ChartErrorBoundary>
            <ComparableSalesTable
              data={comparables}
              guidePrice={property.asking_price}
              propertyType={property.property_type}
              district={property.postcode?.split(' ')[0]}
            />
          </ChartErrorBoundary>
        </motion.div>
      </div>
      </div> {/* end px-6 */}
    </div>
  );
}


// ----------------------------------------------------------------------------
// Comparables-based AVM panel: headline estimate + range + comp list.
// Data comes from GET /api/properties/{id}/comparables.
// ----------------------------------------------------------------------------

function fmtGBP(n) {
  if (n == null) return '—';
  return '£' + Math.round(n).toLocaleString('en-GB');
}

// Evidence tier — the user-facing classification.
// evidenced: directly comparable same-type sales support the figure.
// inferred:  weaker direct evidence — district-wide same-type, or cross-type
//            sales adjusted on a £/sqm basis. Directional, not definitive.
// modelled:  no usable comparable evidence; statistical fallback.
// none:      no Land Registry data at all in the area.
const EVIDENCE_META = {
  evidenced: {
    label:  'Evidenced valuation',
    short:  'Comparable-backed.',
    blurb:  'Backed by recent same-type sales in this postcode — our most defensible figure.',
    colour: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  },
  inferred: {
    label:  'Inferred valuation',
    short:  'Indirectly derived.',
    blurb:  'Inferred from adjusted comparables — wider area or cross-type extrapolation. Directional, not definitive.',
    colour: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  },
  modelled: {
    label:  'Modelled estimate',
    short:  'Statistical fallback.',
    blurb:  'Insufficient comparable evidence — this is a financially modelled estimate, not a market-justified valuation.',
    colour: 'text-orange-400 bg-orange-500/10 border-orange-500/30',
  },
  none: {
    label:  'Limited local data',
    short:  'Sparse local matches.',
    blurb:  'Few directly comparable recent sales in this district — see nearby districts below or the wider estimate if shown.',
    colour: 'text-slate-400 bg-slate-800 border-slate-700',
  },
};

const TIER_LABEL = {
  sector_tight:       'postcode sector · same beds/size',
  sector_wide:        'postcode sector · wider size range',
  district_wide:      'postcode district · loose filters',
  district_wide_raw:  'postcode district · sales without EPC match',
  nearby_district:    'neighbouring district · same type',
  nearby_district_raw:'neighbouring district · wider sales pool',
  cross_type_psqm:    'district £/sqm · different property types',
  district_mean:      'district average · no direct comparables',
  none:               'no matches',
};

const USAGE_OPTIONS = [
  { value: 'residential', label: 'Residential' },
  { value: 'commercial',  label: 'Commercial' },
  { value: 'mixed_use',   label: 'Mixed-use' },
  { value: 'land',        label: 'Land' },
  { value: 'other',       label: 'Other' },
];

function EditableDetails({ property, canEdit, editing, saving, onEdit, onCancel, onSave }) {
  // The latest auction whose status is upcoming/active is the live one.
  const latestAuction = (property.auctions || []).slice().sort((a, b) => (b.id || 0) - (a.id || 0))[0];
  const auctionLive = latestAuction && [null, undefined, 'upcoming', 'active'].includes(latestAuction.sale_status);
  const priceLabel = auctionLive ? 'Guide price' : 'Asking price';
  const currentPrice = auctionLive ? latestAuction.guide_price : property.asking_price;

  const [priceInput, setPriceInput] = useState('');
  const [usageInput, setUsageInput] = useState('');

  useEffect(() => {
    if (editing) {
      setPriceInput(currentPrice != null ? String(currentPrice) : '');
      setUsageInput(property.current_usage || '');
    }
  }, [editing, currentPrice, property.current_usage]);

  const missingPrice = currentPrice == null;

  if (!editing) {
    return (
      <div className={`rounded-xl border p-3 flex items-start justify-between gap-3 ${
        missingPrice ? 'bg-amber-500/5 border-amber-500/30' : 'bg-slate-800/30 border-slate-800'
      }`}>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
            {priceLabel}{missingPrice && ' — missing'}
          </div>
          <div className={`text-lg font-bold ${missingPrice ? 'text-amber-400' : 'text-white'}`}>
            {missingPrice ? 'Add a price' : `£${Number(currentPrice).toLocaleString('en-GB')}`}
          </div>
          {property.current_usage && (
            <div className="text-xs text-slate-400 mt-1">
              Current usage: <span className="text-slate-200">
                {USAGE_OPTIONS.find(u => u.value === property.current_usage)?.label || property.current_usage}
              </span>
            </div>
          )}
        </div>
        {canEdit && (
          <button
            onClick={onEdit}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-700 transition"
          >
            Edit
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
      <div>
        <label className="block text-xs text-slate-400 mb-1">{priceLabel} (£)</label>
        <input
          type="number"
          value={priceInput}
          onChange={e => setPriceInput(e.target.value)}
          placeholder="e.g. 250000"
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Current usage</label>
        <select
          value={usageInput}
          onChange={e => setUsageInput(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
        >
          <option value="">— Not set —</option>
          {USAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onSave(priceInput, usageInput)}
          disabled={saving}
          className="flex-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white text-sm font-semibold py-2 rounded-lg"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-2 text-sm text-slate-400 hover:text-white border border-slate-700 rounded-lg"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


function ComparablesAvm({ avm, loading, guidePrice, locked, lockedHint, upgradeHref, upgradeLabel, property }) {
  if (loading) {
    return (
      <>
        <h2 className="text-white font-semibold mb-1">Indicative Value</h2>
        <p className="text-slate-500 text-sm">Calculating from comparable sales…</p>
      </>
    );
  }

  if (locked) {
    return (
      <div className="opacity-50">
        <h2 className="text-white font-semibold mb-1">Indicative Value</h2>
        <p className="text-slate-500 text-sm mb-3">
          Comparable-sales valuation from Land Registry + EPC matched sales.
        </p>
        <div className="pointer-events-auto text-center border-t border-slate-800 pt-4">
          <p className="text-xs text-slate-500 mb-2">{lockedHint}</p>
          <Link to={upgradeHref} className="text-sm text-emerald-400 hover:text-emerald-300 font-semibold">
            {upgradeLabel}
          </Link>
        </div>
      </div>
    );
  }

  if (!avm) {
    return (
      <>
        <h2 className="text-white font-semibold mb-1">Indicative Value</h2>
        <p className="text-slate-500 text-sm">Could not load comparable sales for this property.</p>
      </>
    );
  }

  const evidence = EVIDENCE_META[avm.evidence_tier] || EVIDENCE_META.none;
  const tier = TIER_LABEL[avm.tier_used] || avm.tier_used;
  const isModelled = avm.evidence_tier === 'modelled';
  const isInferred = avm.evidence_tier === 'inferred';
  const sparse = avm.sparse_explanation;
  const nearby = avm.nearby_suggestions || [];
  const district = property?.postcode?.split(' ')[0];

  // Guide-vs-estimate positioning (when we have a guide price).
  let verdict = null;
  if (guidePrice && avm.estimate_mid) {
    const pct = ((guidePrice - avm.estimate_mid) / avm.estimate_mid) * 100;
    if (pct <= -15) verdict = { text: 'Guide well below estimate', tone: 'text-emerald-400' };
    else if (pct <= -5) verdict = { text: 'Guide below estimate', tone: 'text-emerald-300' };
    else if (pct < 5) verdict = { text: 'Guide in line with estimate', tone: 'text-slate-300' };
    else if (pct < 15) verdict = { text: 'Guide above estimate', tone: 'text-amber-300' };
    else verdict = { text: 'Guide well above estimate', tone: 'text-red-400' };
  }

  return (
    <>
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="text-white font-semibold mb-1">Indicative Value</h2>
          <p className="text-slate-400 text-sm">
            {avm.comparable_count > 0
              ? <>Based on {avm.comparable_count} comparable sold {avm.comparable_count === 1 ? 'home' : 'homes'} · {tier} · last 24 months</>
              : <>Modelled from district sales · {tier} · last 24 months</>}
          </p>
        </div>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full border whitespace-nowrap ${evidence.colour}`}>
          {evidence.label}
        </span>
      </div>

      <ChartDataCallout
        tone={isModelled ? 'amber' : isInferred ? 'amber' : 'emerald'}
        title="What this number means"
        className="mb-4"
      >
        {evidence.blurb}
        <ChartDataCalloutList
          items={[
            'Evidenced — recent same-type sales in the same postcode area.',
            'Inferred — widened search, neighbouring district, or adjusted comparables.',
            'Modelled — statistical fallback when direct comps are thin.',
          ]}
        />
      </ChartDataCallout>

      {avm.search_widened_to && (
        <ChartDataCallout tone="amber" title="Search widened beyond this district" className="mb-4">
          Few closely matched sales in <strong className="text-slate-200">{district || 'this district'}</strong>.
          This estimate uses sales from <strong className="text-slate-200">{avm.search_widened_to}</strong> and
          nearby postcode districts. Extensions or unique homes often need this wider net.
        </ChartDataCallout>
      )}

      {sparse && (
        <ChartDataCallout tone="info" title="Why local matches can be thin" className="mb-4">
          <p>{sparse.summary}</p>
          {sparse.hints?.length > 0 && <ChartDataCalloutList items={sparse.hints} />}
        </ChartDataCallout>
      )}

      {nearby.length > 0 && (
        <div className="mb-4">
          <ChartDataCallout tone="info" title="Nearby districts with similar sales" className="mb-3">
            When this postcode district has little data for this property type, these neighbouring
            areas are the next best reference — sample sales shown below.
          </ChartDataCallout>
          <div className="space-y-2">
            {nearby.map((n) => (
              <div key={n.district} className="bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 text-sm">
                <div className="flex justify-between gap-2 text-slate-200 font-medium">
                  <span>{n.district}</span>
                  <span className="text-slate-400 font-normal">
                    {n.comparable_count} recent {property?.property_type || 'sale'}(s)
                  </span>
                </div>
                {n.sample_sales?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-slate-400 text-xs">
                    {n.sample_sales.map((s, i) => (
                      <li key={i}>
                        {fmtGBP(s.sale_price)} · {s.postcode}
                        {s.sale_date ? ` · ${new Date(s.sale_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {avm.estimate_mid ? (
        <div className="mb-5">
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="text-3xl font-bold text-white">{fmtGBP(avm.estimate_mid)}</span>
            <span className="text-slate-400 text-sm">
              range {fmtGBP(avm.estimate_low)} — {fmtGBP(avm.estimate_high)}
            </span>
          </div>
          {verdict && (
            <p className={`text-sm mt-1 ${verdict.tone}`}>
              {verdict.text}
              {guidePrice && ` (guide ${fmtGBP(guidePrice)})`}
            </p>
          )}
        </div>
      ) : (
        <ChartDataCallout tone="amber" title="No estimate produced" className="mb-4">
          {sparse?.summary || 'Not enough recent comparable sales in this area to produce an estimate.'}
          {!property?.epc_floor_area_sqm && !property?.floor_area_sqm && (
            <p className="mt-2">No floor area on file — EPC data would sharpen matching.</p>
          )}
        </ChartDataCallout>
      )}

      {avm.comparables && avm.comparables.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-500 text-xs uppercase tracking-wider border-b border-slate-800">
                <th className="text-left font-medium py-2 pr-3">Sold</th>
                <th className="text-left font-medium py-2 pr-3">Postcode</th>
                <th className="text-right font-medium py-2 pr-3">Price</th>
                <th className="text-right font-medium py-2 pr-3">Sqm</th>
                <th className="text-right font-medium py-2 pr-3">Rooms</th>
                <th className="text-left font-medium py-2">Built form</th>
              </tr>
            </thead>
            <tbody>
              {avm.comparables.slice(0, 10).map((c, i) => (
                <tr key={c.id ?? i} className="border-b border-slate-800/60">
                  <td className="py-2 pr-3 text-slate-400">{c.sale_date}</td>
                  <td className="py-2 pr-3 text-slate-300">{c.postcode}</td>
                  <td className="py-2 pr-3 text-right text-white font-medium">{fmtGBP(c.sale_price)}</td>
                  <td className="py-2 pr-3 text-right text-slate-400">{c.epc_floor_area_sqm ? Math.round(c.epc_floor_area_sqm) : '—'}</td>
                  <td className="py-2 pr-3 text-right text-slate-400">{c.epc_habitable_rooms ?? '—'}</td>
                  <td className="py-2 text-slate-400">{c.epc_built_form || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {avm.comparable_count > 10 && (
            <p className="text-slate-500 text-xs mt-3">
              Showing top 10 of {avm.comparable_count} matched sales.
            </p>
          )}
        </div>
      )}

      <ChartDataCallout tone="default" title="Disclaimer" className="mt-4">
        Indicative only — from Land Registry sales joined with EPC records where available.
        Not a formal RICS valuation. Until a property sells, every figure is an estimate.
      </ChartDataCallout>
    </>
  );
}
